/**
 * Tag Generation Confirmation Dialog
 * Shows preview and warnings before generating tags
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Tag as TagIcon,
  AutoAwesome as AutoAwesomeIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { LessonV1_SpineTagsPrediction, MathExpression } from '../../types';
import { isLimiterGroupMathtastic } from '../../services/spine.service';
import MathtasticIcon from '../common/MathtasticIcon';
import { mathExpressionKeys, MathExpressionFile } from '../../services/mathExpressions.service';

interface SelectedLimiterGroup {
  description: string;
  isMathtastic: boolean;
  limiters: string[];
  mathExpressions: Array<{
    id: string;
    plainText: string;
    isMissing: boolean;
  }>;
}

interface SelectedCompetency {
  id: string;
  description: string;
  limiterGroups: SelectedLimiterGroup[];
}

interface TagGenerationConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  prediction: LessonV1_SpineTagsPrediction;
  hasExistingTags: boolean;
  isDirty?: boolean;
  loading?: boolean;
}

export default function TagGenerationConfirmDialog({
  open,
  onClose,
  onConfirm,
  prediction,
  hasExistingTags,
  isDirty,
  loading = false,
}: TagGenerationConfirmDialogProps) {
  const queryClient = useQueryClient();

  // Get math expressions from cache (use 'modified' to match ReviewTab's fetch)
  const getMathExpressionsMap = (): Map<string, MathExpression> => {
    const cachedData = queryClient.getQueryData<MathExpressionFile>(
      mathExpressionKeys.modified(prediction.id)
    );
    const map = new Map<string, MathExpression>();
    if (cachedData?.expressions) {
      for (const expr of cachedData.expressions) {
        // Skip malformed expressions that don't have contentExpression
        if (expr?.contentExpression?.id) {
          map.set(expr.contentExpression.id, expr);
        }
      }
    }
    return map;
  };

  // Get selected items for preview
  const getSelectedItems = (): SelectedCompetency[] => {
    const tagCandidates = prediction.tag_prediction_modified || prediction.tag_prediction_original;
    const selectedCompetencies: SelectedCompetency[] = [];
    const mathExpressionsMap = getMathExpressionsMap();

    if (tagCandidates) {
      for (const candidate of tagCandidates) {
        if (candidate.competency.selected) {
          const limiterGroups: SelectedLimiterGroup[] = [];

          for (const group of candidate.limiter_groups) {
            if (group.selected) {
              const isMathtastic = isLimiterGroupMathtastic(group);
              const limiters: string[] = [];
              const mathExpressions: SelectedLimiterGroup['mathExpressions'] = [];

              // Collect selected limiters (skip for mathtastic groups - those use math expressions instead)
              if (!isMathtastic) {
                for (const limiter of group.limiters) {
                  if (limiter.selected) {
                    limiters.push(limiter.description);
                  }
                }
              }

              // If mathtastic, collect math expressions
              if (isMathtastic && group.selected_math_expression_ids) {
                for (const exprId of group.selected_math_expression_ids) {
                  const expr = mathExpressionsMap.get(exprId);
                  if (expr) {
                    mathExpressions.push({
                      id: exprId,
                      plainText: expr.contentExpression.plainText || expr.contentExpression.latex,
                      isMissing: false,
                    });
                  } else {
                    mathExpressions.push({
                      id: exprId,
                      plainText: exprId,
                      isMissing: true,
                    });
                  }
                }
              }

              limiterGroups.push({
                description: group.description,
                isMathtastic,
                limiters,
                mathExpressions,
              });
            }
          }

          selectedCompetencies.push({
            id: candidate.competency.spine_node_id,
            description: candidate.competency.description,
            limiterGroups,
          });
        }
      }
    }

    return selectedCompetencies;
  };

  const selectedCompetencies = getSelectedItems();

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: 400,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          Generate SPINE Tags
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Warning for existing tags */}
        {hasExistingTags && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>This lesson already has SPINE tag IDs.</strong> Generating new tags will replace the existing tag IDs.
              Make sure you want to proceed before continuing.
            </Typography>
          </Alert>
        )}

        {/* Warning for unknown dirty state */}
        {isDirty === undefined && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>The state of tag candidates is unknown.</strong> Consider saving them first before generating SPINE tags
              to ensure the latest selections are used.
            </Typography>
          </Alert>
        )}

        {/* Summary */}
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are about to generate SPINE tags for {selectedCompetencies.length} selected competencies:
        </Typography>

        {/* Selected competencies */}
        {selectedCompetencies.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TagIcon fontSize="small" />
              Competencies ({selectedCompetencies.length})
            </Typography>
            <List dense>
              {selectedCompetencies.map((competency, index) => (
                <ListItem key={competency.id} sx={{ pl: 0, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                    <Chip size="small" label={index + 1} color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={competency.description}
                    secondary={
                      <Box>
                        {competency.limiterGroups.map((group, groupIdx) => (
                          <Box key={groupIdx} sx={{ mt: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {group.isMathtastic && (
                                <MathtasticIcon fontSize="small" sx={{ fontSize: 16 }} />
                              )}
                              {group.description}
                            </Typography>

                            {/* Regular limiters */}
                            {group.limiters.length > 0 && (
                              <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem' }}>
                                {group.limiters.map((limiter, idx) => (
                                  <li key={idx} style={{ marginBottom: '2px' }}>
                                    {limiter}
                                  </li>
                                ))}
                              </Box>
                            )}

                            {/* Math expressions for mathtastic groups */}
                            {group.isMathtastic && group.mathExpressions.length > 0 && (
                              <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem' }}>
                                {group.mathExpressions.map((expr) => (
                                  <li key={expr.id} style={{ marginBottom: '2px' }}>
                                    {expr.isMissing ? (
                                      <Tooltip
                                        title={`Expression ID "${expr.id}" not found in math expressions file`}
                                        arrow
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            color: 'error.main',
                                          }}
                                        >
                                          <WarningIcon sx={{ fontSize: 14 }} />
                                          Missing: {expr.id}
                                        </Box>
                                      </Tooltip>
                                    ) : (
                                      expr.plainText
                                    )}
                                  </li>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    }
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* No selections warning */}
        {selectedCompetencies.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No competencies are selected. Please select at least one competency with limiters before generating tags.
            </Typography>
          </Alert>
        )}

        {/* Process explanation */}
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>What happens next:</strong>
            <br />
            1. Selected competencies and limiters will be sent to the SPINE API
            <br />
            2. Math expressions from P-type groups will be included as regex targets
            <br />
            3. SPINE tag IDs will be generated and stored with this lesson
            <br />
            4. You can then save your changes to finalize the tags
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading || selectedCompetencies.length === 0}
          variant="contained"
          startIcon={loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <AutoAwesomeIcon />
          )}
          sx={{ minWidth: 180 }}
        >
          {loading ? 'Generating...' :
           isDirty === true ? 'Save & Generate Tags' :
           'Generate Tags'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
